import { useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  selectVisibleUnreadCount,
  selectVisibleNotifications,
  markAsRead,
  markAllAsRead,
  hydrateNotifications,
} from "../../store/slices/notificationsSlice";
import { notificationService } from "../../services/api";
import { resolveNotificationPath } from "../../utils/notificationPath";
import { contextTargetRole } from "../../utils/notificationContext";
import { switchRole } from "../../store/slices/authSlice";

export const useHeaderNotifications = (isNotificationOpen) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Perspective-filtered (D-011): AgencyAdmin hides Messages + Meetings.
  const unreadCount = useSelector(selectVisibleUnreadCount);
  const notifications = useSelector(selectVisibleNotifications);
  // Hydrate server history once the user is authenticated. localStorage is now
  // a cache only; the server is the source of truth and read-state (`seen`)
  // syncs across devices.
  const isAuthenticated = useSelector(
    (state) => !!state.auth.tokens?.access
  );
  // Dual-profile users (Agent + AgencyAdmin): a click on a notification that
  // belongs to the OTHER profile context first switches the profile (same
  // auth/switchRole mechanism as the manual profile switcher) and then
  // navigates — never a wrong-context landing, never a dead end (W1-02).
  const authRoles = useSelector((state) => state.auth && state.auth.roles);
  const currentRole = useSelector(
    (state) => state.auth && state.auth.currentRole
  );

  // One shared server refetch: the merge in the slice dedups by id and never
  // toasts, so this is safe to run alongside live websocket arrivals.
  const refreshFromServer = useCallback(async () => {
    const res = await notificationService.getAllNotifications();
    if (res?.success && Array.isArray(res.data)) {
      // Pass the server-side total along: the badge must count ALL unseen
      // rows, not just the first page the bell loads.
      dispatch(
        hydrateNotifications({
          list: res.data,
          unreadCount: res.pagination?.unread_count,
        })
      );
    }
  }, [dispatch]);

  // Load the server-side notification history on mount (and whenever auth
  // becomes available).
  useEffect(() => {
    if (!isAuthenticated) return;
    refreshFromServer();
  }, [isAuthenticated, refreshFromServer]);

  // While the dropdown is open: keep the websocket connected (future events)
  // AND re-hydrate once (rows created while the socket was closed — the
  // socket only delivers from now on, it cannot replay what was missed).
  // Header shares isNotificationOpen with MobileHeader, so this covers both
  // bells with exactly one fetch per open.
  useEffect(() => {
    if (isNotificationOpen) {
      window.dispatchEvent(new CustomEvent("notifications:connect"));
      if (isAuthenticated) refreshFromServer();
    } else {
      window.dispatchEvent(new CustomEvent("notifications:disconnect"));
    }
  }, [isNotificationOpen, isAuthenticated, refreshFromServer]);

  const handleNotificationClick = (notificationId) => {
    if (!notificationId) return;

    const notificationObj = notifications.find((n) => n.id === notificationId);

    // Mark read locally & backend
    dispatch(markAsRead(notificationId));
    notificationService.markNotificationAsRead(notificationId);

    // Deep-link resolution (shared resolver): server redirect_path first, then
    // the agent-tender + legacy meeting fallbacks. When nothing resolves, open
    // the full notifications page so the click is never a dead end.
    if (notificationObj) {
      // Cross-context click → switch the profile BEFORE navigating (W1-02).
      const targetRole = contextTargetRole(
        notificationObj,
        authRoles,
        currentRole
      );
      if (targetRole) {
        dispatch(switchRole(targetRole));
      }
      navigate(resolveNotificationPath(notificationObj) || "/notifications");
    }
  };

  const handleMarkAllAsRead = async () => {
    dispatch(markAllAsRead());
    try {
      await notificationService.markAllNotificationsAsRead();
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const formatNotificationTime = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600)
      return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  // Get recent notifications (last 10)
  const recentNotifications = notifications.slice(0, 10);

  return {
    unreadCount,
    notifications,
    recentNotifications,
    handleNotificationClick,
    handleMarkAllAsRead,
    formatNotificationTime,
  };
};
